<script setup>
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/ui/dialog";
import Button from "@/shared/ui/button/Button.vue";
import { Spinner } from "@/shared/ui/spinner";
import SvgIcon from "@/shared/ui/svg-icon/SvgIcon.vue";
import { InfoGroup } from "@/shared/ui/info-group";
import {
  ensureStatusDictionary,
  getStatusLabel,
} from "@/shared/config/statusDictionary";

const props = defineProps({
  open: { type: Boolean, default: false },
  type: { type: String, default: "testament" },
  result: { type: Object, default: null },
});

const emit = defineEmits(["update:open", "closed"]);

const { t } = useI18n();

ensureStatusDictionary();

const title = computed(() => {
  if (props.type === "testament") return t("Поиск завещания");
  if (props.type === "statement") return t("Поиск заявления");
  return t("Поиск наследственного дела");
});

function handleOpenChange(val) {
  emit("update:open", val);
  if (!val) {
    copyState.value = "idle";
    emit("closed");
  }
}

const infoGroups = computed(() => {
  const d = props.result?.data;
  if (!d) return [];

  const groups = [];

  const caseItems = [
    { label: t("Наследодатель"), value: d.deceased_fio },
    { label: t("№ дела"), value: d.existing_nd_number },
    {
      label: t("Статус"),
      value: getStatusLabel(d.existing_nd_status) || d.existing_nd_status,
    },
  ].filter((i) => i.value);

  if (caseItems.length) {
    groups.push({ title: t("Данные дела"), columns: 2, items: caseItems });
  }

  const notary = d.notary || {};
  const notaryItems = [
    { label: t("Нотариус"), value: notary.fio || d.notary_name },
    { label: t("Статус лицензии"), value: notary.license_status },
    { label: t("Телефон"), value: notary.phone || d.notary_phone },
    { label: "Email", value: notary.email || d.notary_email },
    { label: t("Адрес"), value: notary.address || d.notary_address },
  ].filter((i) => i.value);

  if (notaryItems.length) {
    groups.push({
      title: t("Данные нотариуса"),
      columns: 2,
      items: notaryItems,
    });
  }

  // Примечание + наследники в одной группе
  const noteItems = [];

  if (d.result?.note_ru) {
    noteItems.push({ label: t("Примечание"), value: d.result.note_ru });
  }

  const heirs = d.participants?.filter(
    (p) => p.participant_type_code !== "NASLEDODATEL"
  );
  if (heirs?.length) {
    heirs.forEach((p, i) => {
      const fio = [p.last_name, p.first_name, p.middle_name]
        .filter(Boolean)
        .join(" ");
      if (fio) {
        noteItems.push({
          label: `${t("Наследник")} ${i + 1}`,
          value: fio,
        });
      }
    });
  }

  if (noteItems.length) {
    groups.push({
      title: t("Примечание"),
      columns: 1,
      items: noteItems,
    });
  }

  return groups;
});

const copyState = ref("idle");

async function doCopy(text) {
  copyState.value = "copying";
  try {
    await navigator.clipboard.writeText(text);
    await new Promise((r) => setTimeout(r, 600));
    copyState.value = "copied";
  } catch {
    copyState.value = "idle";
  }
}

function copyInfo() {
  if (!props.result?.data) return;
  const lines = infoGroups.value.flatMap((g) => [
    g.title,
    ...g.items.map((i) => `${i.label}: ${i.value}`),
    "",
  ]);
  doCopy(lines.join("\n").trim());
}

function copyContacts() {
  if (!props.result?.data) return;
  const d = props.result.data;
  const notary = d.notary || {};
  const parts = [
    (notary.fio || d.notary_name) &&
      `${t("Нотариус")}: ${notary.fio || d.notary_name}`,
    (notary.phone || d.notary_phone) &&
      `${t("Телефон")}: ${notary.phone || d.notary_phone}`,
    (notary.email || d.notary_email) &&
      `Email: ${notary.email || d.notary_email}`,
    (notary.address || d.notary_address) &&
      `${t("Адрес")}: ${notary.address || d.notary_address}`,
  ].filter(Boolean);
  doCopy(parts.join("\n"));
}
</script>

<template>
  <Dialog :open="open" @update:open="handleOpenChange">
    <DialogContent class="sm:max-w-[600px]">
      <DialogHeader>
        <DialogTitle>{{ title }}</DialogTitle>
      </DialogHeader>

      <div class="flex flex-col gap-3 py-2">
        <template v-if="result?.found">
          <div
            v-if="infoGroups.length"
            class="p-3 rounded-lg border border-border"
          >
            <InfoGroup :groups="infoGroups" />
          </div>
        </template>

        <template v-else-if="result">
          <div class="p-3 rounded-lg bg-green-50 flex items-start gap-3">
            <span class="text-lg mt-0.5">✅</span>
            <div class="flex flex-col gap-1">
              <span class="text-sm font-medium">
                {{ result?.message || $t("Не найдено") }}
              </span>
            </div>
          </div>
        </template>
      </div>

      <DialogFooter v-if="result?.found && result?.data">
        <Button
          variant="outline"
          size="sm"
          class="gap-2 transition-all"
          :disabled="copyState === 'copying'"
          @click="type === 'testament' ? copyContacts() : copyInfo()"
        >
          <Spinner v-if="copyState === 'copying'" size="sm" />
          <svg-icon
            v-else-if="copyState === 'copied'"
            name="huge-check-mark"
            class="w-4 h-4 text-green-500"
          />
          {{
            copyState === "copied"
              ? $t("Скопировано")
              : type === "testament"
              ? $t("Скопировать контакты нотариуса")
              : $t("Скопировать информацию")
          }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
